/**
 * Local task persistence — stores background task metadata in ~/.arbi/tasks.json.
 *
 * Simple JSON file, same pattern as session/config files.
 * Auto-prunes tasks older than 7 days on read.
 */

import fs from 'node:fs'
import path from 'node:path'
import os from 'node:os'

export interface StoredTask {
  id: string
  question: string
  workspaceId: string
  submittedAt: string
  status: 'queued' | 'in_progress' | 'completed' | 'failed'
  model?: string | null
}

const MAX_TASKS = 50
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

function getTasksFile(): string {
  const configDir = process.env.ARBI_CONFIG_DIR ?? path.join(os.homedir(), '.arbi')
  return path.join(configDir, 'tasks.json')
}

function ensureDir(filePath: string): void {
  const dir = path.dirname(filePath)
  if (!fs.existsSync(dir)) {
    fs.mkdirSync(dir, { recursive: true, mode: 0o700 })
  }
}

function readTasks(): StoredTask[] {
  try {
    const content = fs.readFileSync(getTasksFile(), 'utf-8')
    return JSON.parse(content) as StoredTask[]
  } catch {
    return []
  }
}

function writeTasks(tasks: StoredTask[]): void {
  const filePath = getTasksFile()
  ensureDir(filePath)
  fs.writeFileSync(filePath, JSON.stringify(tasks, null, 2) + '\n', { mode: 0o600 })
}

/** Read tasks, auto-pruning entries older than 7 days. */
export function getTasks(): StoredTask[] {
  const now = Date.now()
  const tasks = readTasks().filter((t) => now - new Date(t.submittedAt).getTime() < MAX_AGE_MS)
  return tasks
}

/** Add a task (most recent first), cap at MAX_TASKS. */
export function addTask(task: StoredTask): void {
  const tasks = [task, ...getTasks().filter((t) => t.id !== task.id)].slice(0, MAX_TASKS)
  writeTasks(tasks)
}

/** Update the status of a stored task. */
export function updateTaskStatus(id: string, status: StoredTask['status']): void {
  const tasks = readTasks()
  const task = tasks.find((t) => t.id === id)
  if (task) {
    task.status = status
    writeTasks(tasks)
  }
}

/** Get the most recently submitted task. */
export function getLatestTask(): StoredTask | null {
  const tasks = getTasks()
  return tasks[0] ?? null
}
